import { styled } from "@mui/material/styles";
import { AppBar, Box, Stack, Toolbar } from "@mui/material";
import useResponsive from "src/hooks/useResponsive";
import cssStyles from "src/utils/cssStyles";
import { HEADER, NAVBAR } from "src/config/config";
import Iconify from "src/components/Iconify";
import { IconButtonAnimate } from "src/components/animate";
import Searchbar from "./Searchbar";
import LanguagePopover from "./LanguagePopover";
import NotificationsPopover from "./NotificationsPopover";
import AccountPopover from "./AccountPopover";
import { LogoutIcon } from "src/theme/overrides/CustomIcons";
import { PATH_LOGOUT } from "src/routes/paths";
import { useNavigate } from "react-router";

type RootStyleProps = {
    isCollapse: boolean;
    verticalLayout: boolean;
};

const RootStyle = styled(AppBar, {
    shouldForwardProp: (prop) => prop !== "isCollapse" && prop !== "verticalLayout",
})<RootStyleProps>(({ isCollapse, verticalLayout, theme }) => ({
    ...cssStyles(theme).bgBlur(),
    boxShadow: "none",
    height: HEADER.MOBILE_HEIGHT,
    borderBottom: `1px solid ${theme.palette.divider}`,
    zIndex: theme.zIndex.appBar + 1,
    transition: theme.transitions.create(["width", "height"], {
        duration: theme.transitions.duration.shorter,
    }),
    [theme.breakpoints.up("lg")]: {
        height: HEADER.DASHBOARD_DESKTOP_HEIGHT,
        width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH + 1}px)`,
        ...(isCollapse && {
            width: `calc(100% - ${NAVBAR.DASHBOARD_COLLAPSE_WIDTH}px)`,
        }),
        ...(verticalLayout && {
            width: "100%",
            height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
            backgroundColor: theme.palette.background.default,
        }),
    },
}));

type Props = {
    onOpenSidebar: VoidFunction;
    isCollapse?: boolean;
    verticalLayout?: boolean;
};

export default function Header({
    onOpenSidebar,
    isCollapse = false,
    verticalLayout = false,
}: Props) {
    const isDesktop = useResponsive("up", "lg");
    const navigate = useNavigate();

    return (
        <RootStyle isCollapse={isCollapse} verticalLayout={verticalLayout}>
            <Toolbar
                sx={{
                    minHeight: "100% !important",
                    px: { lg: 5 },
                }}
            >
                {!isDesktop && (
                    <IconButtonAnimate
                        onClick={onOpenSidebar}
                        sx={{ mr: 1, color: "text.primary" }}
                    >
                        <Iconify icon="eva:menu-2-fill" />
                    </IconButtonAnimate>
                )}
                <Searchbar />
                <Box sx={{ flexGrow: 1 }} />

                <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1.5 }}>
                    <LanguagePopover />
                    <NotificationsPopover />
                    <AccountPopover />
                    <IconButtonAnimate
                        onClick={() => navigate(PATH_LOGOUT)}
                        sx={{ width: 40, height: 40 }}
                    >
                        <LogoutIcon sx={{ width: 20, height: 20 }} />
                    </IconButtonAnimate>
                </Stack>
            </Toolbar>
        </RootStyle>
    );
}
